/** Public promotion data shared by server rendering and browser refreshes. */
export interface PublicPromotion {
  id: number;
  slug: string;
  title: string;
  description: string;
  icon: string;
  accent: string;
  detail_key: string | null;
  sort_order: number;
  legal_note: string | null;
  terms: string[];
  /**
   * Hero cover uploaded in admin for the site. Optional because the storefront
   * reads the raw response, and an older backend does not send these fields.
   */
  web_image_desktop?: string | null;
  web_image_mobile?: string | null;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function toStr(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function toNum(value: unknown, fallback = 0): number {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function normalizePromotion(raw: unknown): PublicPromotion | null {
  const r = asRecord(raw);
  const slug = toStr(r.slug).trim();
  const title = toStr(r.title).trim();
  if (!slug || !title) return null;

  return {
    id: toNum(r.id),
    slug,
    title,
    description: toStr(r.description).trim(),
    icon: toStr(r.icon, 'gift'),
    accent: toStr(r.accent, 'violet-deep'),
    detail_key: toStr(r.detail_key).trim() || null,
    sort_order: toNum(r.sort_order),
    legal_note: toStr(r.legal_note).trim() || null,
    terms: Array.isArray(r.terms)
      ? r.terms.map((term) => toStr(term).trim()).filter((term) => term.length > 0)
      : [],
    web_image_desktop: toStr(r.web_image_desktop).trim() || null,
    web_image_mobile: toStr(r.web_image_mobile).trim() || null,
  };
}

/**
 * An empty array is authoritative: all offers may have been disabled in admin.
 * null means an invalid response, so callers can offer a retry without showing
 * old offers or inventing replacement terms.
 */
export function normalizePromotions(data: unknown): PublicPromotion[] | null {
  if (!Array.isArray(data)) return null;
  return data
    .map(normalizePromotion)
    .filter((promo): promo is PublicPromotion => promo !== null)
    .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
}
